/**
 * Bundle Monaco from npm for Vite hosts. Without this, @monaco-editor/react
 * loads AMD Monaco from a CDN and the editor sits on "Loading..." forever when
 * the CDN is blocked (typical on corporate beta hosts).
 *
 * Language workers are registered via Vite `?worker` imports when available
 * (see monacoWorkers.ts). CSP must allow `worker-src 'self' blob:` (already set
 * in platform Caddy). If worker loading/construction fails, we log once and
 * continue without workers (main-thread editing still works).
 */
import { loader } from '@monaco-editor/react';
import * as monaco from 'monaco-editor';
import {
  createMonacoWorkerFactory,
  type MonacoWorkerCtors
} from './monacoWorkerFactory';

export {
  createMonacoWorkerFactory,
  type MonacoWorkerConstructor,
  type MonacoWorkerCtors
} from './monacoWorkerFactory';

loader.config({ monaco });

let workerFallbackLogged = false;
let environmentInstalled = false;

function logWorkerFallback(err: unknown): void {
  if (workerFallbackLogged) return;
  workerFallbackLogged = true;
  console.warn(
    "[monaco] Language workers unavailable; continuing without workers. CSP needs worker-src 'self' blob:.",
    err
  );
}

/** Install MonacoEnvironment.getWorker once (injectable workers for tests). */
export function installMonacoWorkerEnvironment(workers: MonacoWorkerCtors): void {
  if (environmentInstalled) return;

  const getWorker = createMonacoWorkerFactory(workers);
  const globalTarget = globalThis as typeof globalThis & {
    MonacoEnvironment?: { getWorker?: (workerId: string, label: string) => Worker };
  };

  globalTarget.MonacoEnvironment = {
    getWorker(workerId: string, label: string): Worker {
      try {
        return getWorker(workerId, label);
      } catch (err) {
        logWorkerFallback(err);
        throw err;
      }
    }
  };

  environmentInstalled = true;
}

let initPromise: Promise<typeof monaco> | null = null;

/** Idempotent: kick off local Monaco init once. */
export function ensureMonacoReady(): Promise<typeof monaco> {
  if (!initPromise) {
    initPromise = (async () => {
      try {
        const { monacoWorkerCtors } = await import('./monacoWorkers');
        installMonacoWorkerEnvironment(monacoWorkerCtors);
      } catch (err) {
        logWorkerFallback(err);
      }
      return loader.init();
    })();
  }
  return initPromise;
}
